import React from 'react';
import { Badge, Button, Select, Spinner } from 'flowbite-react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import type { WorkflowExecutionSummary } from '../../api/workflowInspector';
import { formatDuration, getStatusColor } from '../../api/workflowInspector';

interface ExecutionListProps {
  executions: WorkflowExecutionSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  total: number;
  hasMore: boolean;
  page: number;
  onPageChange: (page: number) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
}

const PAGE_SIZE = 25;

const ExecutionList: React.FC<ExecutionListProps> = ({
  executions,
  selectedId,
  onSelect,
  loading,
  total,
  hasMore,
  page,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="p-3 border-b border-gray-200">
        <Select
          sizing="sm"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="pending">Pending</option>
        </Select>
        <p className="text-xs text-gray-400 mt-1">{total} execution{total !== 1 ? 's' : ''}</p>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && executions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : executions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No executions found.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {executions.map((exec) => (
              <button
                key={exec.Execution_ID}
                onClick={() => onSelect(exec.Execution_ID)}
                className={`w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors ${
                  selectedId === exec.Execution_ID
                    ? 'border-l-4 border-indigo-500 bg-indigo-50'
                    : 'border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {exec.Workflow_Name}
                  </span>
                  <Badge color={getStatusColor(exec.Status)} size="xs">
                    {exec.Status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">
                    {new Date(exec.Started_At).toLocaleDateString()} {new Date(exec.Started_At).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatDuration(exec.Duration_Ms)}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {exec.Completed_Steps}/{exec.Total_Steps} steps
                  {exec.Failed_Steps > 0 && (
                    <span className="text-red-500 ml-1">({exec.Failed_Steps} failed)</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="p-2 border-t border-gray-200 flex items-center justify-between">
        <Button
          size="xs"
          color="gray"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
        >
          <HiChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs text-gray-500">
          {page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
        </span>
        <Button
          size="xs"
          color="gray"
          disabled={!hasMore}
          onClick={() => onPageChange(page + 1)}
        >
          <HiChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export { PAGE_SIZE };
export default ExecutionList;
